import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { mensajeErrorAccion } from '../../core/http-errors';
import { CredencialesAdminService, TipoCuenta } from '../../services/credenciales-admin.service';
import { PermisosService } from '../../services/permisos.service';
import { AlertService } from '../alert/alert.service';
import { AlertButtonType } from '../dialog-alert/AlertButtonType.enum';
import { DialogAlertService } from '../dialog-alert/dialog-alert.service';

export const MIN_PASSWORD = 8;
export const MAX_PASSWORD_BYTES = 72;

/** Mensaje de validación de la nueva contraseña ('' si es válida). Mismas reglas que la API (mínimo 8, máximo 72 bytes de bcrypt). */
export function errorPassword(valor: string): string {
  if (!valor || !valor.trim()) return 'Indica la nueva contraseña.';
  if (valor.length < MIN_PASSWORD) return `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres.`;
  if (new TextEncoder().encode(valor).length > MAX_PASSWORD_BYTES) return `La contraseña es demasiado larga (máximo ${MAX_PASSWORD_BYTES} caracteres; los acentos y símbolos cuentan más).`;
  return '';
}

/**
 * Restablecimiento administrativo de la contraseña de OTRA cuenta (asociación o persona): un único campo, con mostrar/ocultar,
 * confirmación antes de aplicar, estado de carga sin envíos duplicados y resultado claro. Solo lo ofrecen las fichas a quien tiene
 * el permiso global `contrasenas:restablecer` (la API lo vuelve a exigir). No modifica el CIF/NIF, el estado, el QR ni otros datos.
 */
@Component({
  selector: 'app-restablecer-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './restablecer-password.component.html',
  // Apilamiento propio: las fichas tienen un panel blanco absoluto posterior en el DOM que tapa cualquier contenido sin z-index.
  styles: [':host { display: block; position: relative; z-index: 2; }']
})
export class RestablecerPasswordComponent {
  @Input({ required: true }) tipo!: TipoCuenta;
  @Input({ required: true }) cuenta!: { id: number; nombre?: string | null };
  @Output() restablecida = new EventEmitter<void>();

  readonly password = new FormControl('', { nonNullable: true });
  mostrar = false;
  guardando = false;
  intentado = false;

  constructor(
    private readonly servicio: CredencialesAdminService,
    private readonly dialogo: DialogAlertService,
    private readonly alertas: AlertService,
    private readonly permisos: PermisosService
  ) {}

  get puede(): boolean { return this.permisos.tieneGlobal('contrasenas:restablecer'); }
  get error(): string { return errorPassword(this.password.value); }
  get mostrarError(): boolean { return (this.intentado || this.password.touched) && this.error !== ''; }
  get nombre(): string { return this.cuenta?.nombre ? '«' + this.cuenta.nombre + '»' : (this.tipo === 'asociacion' ? 'la asociación' : 'la persona'); }

  textoConfirmacion(): string {
    const efecto = this.tipo === 'asociacion'
      ? 'No cambia el CIF, el estado de la asociación ni el acceso por QR.'
      : 'No cambia el NIF, los datos personales, los cargos ni el histórico.';
    return `Vas a restablecer la contraseña de ${this.nombre}.\n\nLa contraseña anterior dejará de funcionar y las sesiones abiertas con ella tendrán que iniciar sesión de nuevo. ${efecto}\n\nLa operación queda registrada en la auditoría. ¿Quieres continuar?`;
  }

  solicitar(): void {
    this.intentado = true;
    this.password.markAsTouched();
    if (this.guardando || !this.puede || this.error) return; // sin petición si no es válido o ya hay una en curso
    this.dialogo.openDialogAlert({
      title: 'Restablecer contraseña',
      content: this.textoConfirmacion(),
      buttonsAlert: [AlertButtonType.Cancelar, AlertButtonType.Aceptar]
    }).afterClosed().subscribe(respuesta => {
      if (respuesta === AlertButtonType.Aceptar) this.aplicar();
    });
  }

  private aplicar(): void {
    if (this.guardando || this.error) return;
    this.guardando = true;
    this.servicio.restablecer(this.tipo, this.cuenta.id, this.password.value).subscribe({
      next: () => {
        this.guardando = false;
        this.password.reset('');
        this.mostrar = false;
        this.intentado = false;
        this.alertas.success(`La contraseña de ${this.nombre} se ha restablecido.`, 5000);
        this.restablecida.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.guardando = false;
        this.alertas.danger(mensajeErrorAccion(err, 'No se ha podido restablecer la contraseña.'), 6000);
      }
    });
  }
}
